import { type ComponentProps } from "react";
import {
  CheckCircleIcon,
  ClockIcon,
  PauseIcon,
} from "@heroicons/react/outline";
import { CheckIcon, XIcon } from "@heroicons/react/solid";

export type Status =
  | "NEW"
  | "TRIAL"
  | "INCOMPLETE"
  | "ACTIVE"
  | "PAST_DUE"
  | "PENDING_CANCELLATION"
  | "CANCELLED"
  | "ENDED"
  | "TERMINATED"
  | "PAUSED";

type SubscriptionsStatusBadgeProps = {
  status: Status;
};

export default function SubscriptionStatusBadge({
  status,
}: SubscriptionsStatusBadgeProps) {
  const { label, Icon, className } = statusMetaMap[status];

  return (
    <span
      className={`inline-flex items-center space-x-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${className}`}
    >
      <span>{label}</span>
      <Icon className="h-3 w-3 text-current" aria-hidden="true" />
    </span>
  );
}

const statusMetaMap: Record<
  Status,
  {
    label: string;
    Icon: (_props: ComponentProps<"svg">) => JSX.Element;
    className: string;
  }
> = {
  NEW: {
    label: "Incomplete",
    Icon: ClockIcon,
    className: "bg-foundations-subtler text-foundations-bold",
  },
  TRIAL: {
    label: "On trial",
    Icon: ClockIcon,
    className: "bg-foundations-subtler text-foundations-bold",
  },
  INCOMPLETE: {
    label: "Incomplete",
    Icon: ClockIcon,
    className: "bg-foundations-subtler text-foundations-bold",
  },
  PAST_DUE: {
    label: "Past due",
    Icon: ClockIcon,
    className: "text-danger bg-danger-bold/[12%]",
  },
  ACTIVE: {
    label: "Active",
    Icon: CheckIcon,
    className: "text-success bg-success-bold/[12%]",
  },
  PAUSED: {
    label: "Paused",
    Icon: PauseIcon,
    className: "text-gold-400 bg-warning-bold/[16%]",
  },
  CANCELLED: {
    label: "Cancelled",
    Icon: XIcon,
    className: "text-danger bg-danger-bold/[12%]",
  },
  PENDING_CANCELLATION: {
    label: "Cancel pending",
    Icon: ClockIcon,
    className: "bg-foundations-subtler text-foundations-bold",
  },
  TERMINATED: {
    label: "Terminated",
    Icon: XIcon,
    className: "text-danger bg-danger-bold/[12%]",
  },
  ENDED: {
    label: "Ended",
    Icon: CheckCircleIcon,
    className: "bg-foundations-subtler text-foundations-bold",
  },
};
